// src/services/api/download.js
// Downloads from gated gateway routes. Every dashboard-facing proxy (/management*, /agents/*,
// the scanners) sits behind a gate that needs the bearer header only the axios interceptor
// adds, so a plain <a href> or a navigation to the gateway URL gets 401 "Sign-in required"
// (feature-roles spec 2026-10-06 §5.1; management spec 2026-09-30 §10.5). A download is
// therefore a blob fetch through gatewayClient, saved from an object URL. The one public
// exception, the MSR /pdf/:pdfId link, is exempt at the gateway and still works as a link.
import gatewayClient from './gateway.js';

// The server names the file; RFC 6266 plain and UTF-8'' forms both occur.
export function filenameFromDisposition(header, fallback) {
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header || '');
  if (!m) return fallback;
  try { return decodeURIComponent(m[1]); } catch { return m[1]; }
}

// How long an object URL outlives its click. Safari and some Firefox builds resolve the blob:
// URL after the click handler returns, so revoking in the same tick can cancel the download;
// FileSaver.js waits 40 s for the same reason. A workbook is a few MB at most; holding it is free.
const REVOKE_DELAY_MS = 40000;

// Saves a blob the way a download link would, without a link. The anchor goes at once; the
// object URL is revoked after REVOKE_DELAY_MS.
export function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  try { a.click(); } finally {
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  }
}

// A blob request turns the error body into a Blob too, which hides FastAPI's `detail` (or the
// gateway's `message`) from the toast helpers. Parse it back to JSON in place; a body that is
// not JSON, or not a Blob, stays as it was.
export async function unblobErrorBody(e) {
  const data = e?.response?.data;
  if (typeof Blob === 'undefined' || !(data instanceof Blob)) return;
  try { e.response.data = JSON.parse(await data.text()); } catch { /* not JSON: keep the Blob */ }
}

// GET `url` as a blob with the bearer client and save it under the server's filename, else
// `fallbackName`. `timeout` (ms) overrides the client's 30 s for a slow export. Resolves to the
// filename; rejects with the original axios error, its body un-blobbed for detailText/errorText.
export async function downloadFile(url, { fallbackName, timeout } = {}) {
  let res;
  try {
    res = await gatewayClient.get(url, { responseType: 'blob', ...(timeout ? { timeout } : {}) });
  } catch (e) {
    await unblobErrorBody(e);
    throw e;
  }
  const filename = filenameFromDisposition(res.headers?.['content-disposition'], fallbackName);
  saveBlob(res.data, filename);
  return filename;
}
